import { html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { ArrowDirection } from '../ui-arrow-button/ui-arrow-button';
import styles from './ui-accordion-item.css?inline';
import '../ui-icons/ui-chevron-arrow-icon';

const CName = 'ui-accordion-item';

/**
 * @prop {string} ariaLabel - Aria-label for accessibility purposes.
 * @prop {string} arrowDirection - Direction in which the arrow points. It can be 'down', 'left', 'right', or 'up'.
 */
@customElement(CName)
export class UIAccordionItem extends LitElement {
  static readonly styles = unsafeCSS(styles);

  @property({ type: Boolean }) isPreOpened = !!window.$app.appConfig.prerenderMode;
  @property({ type: Boolean }) isDisabled = false;
  @property({ type: Function }) onToggle: (isOpen: boolean) => void;
  @state() _isAccordionOpened = false;
  @query('.accordion-content') private _contentEl!: HTMLDivElement;

  private static _nextId = 0;
  private readonly _contentId = `accordion-${UIAccordionItem._nextId++}`;

  connectedCallback() {
    super.connectedCallback();
    this._isAccordionOpened = this.isPreOpened;
  }

  firstUpdated() {
    const el = this._contentEl;
    if (this._isAccordionOpened) {
      el.style.height = 'auto';
      el.style.opacity = '1';
    } else {
      el.style.height = '0px';
      el.style.opacity = '0';
    }
  }

  render() {
    return html`
      <div class="accordion-item">
        <button
          class="accordion-title"
          @click=${this._toggleAccordion}
          ?disabled=${this.isDisabled}
          aria-expanded=${this._isAccordionOpened}
          aria-controls=${this._contentId}
        >
          <slot name="title"></slot>
          ${
            this.isDisabled
              ? nothing
              : html`
                <ui-chevron-arrow-icon
                  tabindex="-1"
                  class="s accordion-icon"
                  arrowDirection=${this._isAccordionOpened ? ArrowDirection.UP : ArrowDirection.DOWN}
                ></ui-chevron-arrow-icon>`
          }
        </button>
        <div id=${this._contentId} class="accordion-content ${this._isAccordionOpened ? 'opened' : ''}"
          ?inert=${!this._isAccordionOpened}
        >
          <slot name="content"></slot>
        </div>
        <slot name="footer"></slot>
      </div>
    `;
  }

  private _toggleAccordion() {
    if (this.isDisabled) return;

    const willOpen = !this._isAccordionOpened;
    this._isAccordionOpened = willOpen;
    this.onToggle?.(willOpen);

    this.updateComplete.then(() => this._animateContent(willOpen));
  }

  private _animateContent(open: boolean) {
    const el = this._contentEl;

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
      el.style.height = open ? 'auto' : '0px';
      el.style.opacity = open ? '1' : '0';
      return;
    }

    const onEnd = (e: TransitionEvent) => {
      if (e.propertyName !== 'height') return;
      if (open) el.style.height = 'auto';
      el.removeEventListener('transitionend', onEnd);
    };

    if (open) {
      el.removeEventListener('transitionend', onEnd);
      el.style.opacity = '1';
      el.style.height = '0px';
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      el.offsetHeight;
      const target = el.scrollHeight;
      el.style.height = `${target}px`;

      el.addEventListener('transitionend', onEnd);
    } else {
      el.removeEventListener('transitionend', onEnd);
      const current = el.scrollHeight;
      el.style.height = `${current}px`;
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      el.offsetHeight;
      el.style.height = '0px';
      el.style.opacity = '0';
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UIAccordionItem;
  }
}
